import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  dateFnsFormatMap,
  dateFnsMonthDayFormatMap
} from '@link/share/constants';
import { DEFAULT_LOCALE } from '@link/share/i18n';
import { parseDate } from '@link/share/utils';

export const useDateFormat = () => {
  const { t, i18n } = useTranslation();

  const formatYearMonth = (date: Date | number) => {
    if (!date) return '';

    const currentLang = i18n.language;
    const pattern =
      dateFnsFormatMap[currentLang as keyof typeof dateFnsFormatMap] ||
      dateFnsFormatMap[DEFAULT_LOCALE];

    return format(date, pattern);
  };

  // Formats an ISO date string ("yyyy-MM-dd") as a locale-specific month/day,
  // e.g. "6月19日" (ja/zh/zt), "June 19" (en), "6월 19일" (ko).
  const formatMonthDay = (date: string) => {
    if (!date) return '';

    const currentLang = i18n.language;
    const pattern =
      dateFnsMonthDayFormatMap[
        currentLang as keyof typeof dateFnsMonthDayFormatMap
      ] || dateFnsMonthDayFormatMap[DEFAULT_LOCALE];

    return format(parseDate(date), pattern);
  };

  // Formats an ISO date string ("yyyy-MM-dd") as a locale-specific month/day
  // with weekday, e.g. "8月16日 (土)" (ja), "8/16 (Sat)" (en). Shares the
  // price-table keys so every surface spells the same date the same way.
  const formatMonthDayWeekday = (date: string) => {
    if (!date) return '';

    // parseDate treats YYYY-MM-DD as a local date; new Date(date) would
    // parse it as UTC and shift the displayed day west of UTC.
    const dateObj = parseDate(date);
    return t('share.priceTableDialog.dateFormat', {
      month: dateObj.getMonth() + 1,
      day: dateObj.getDate(),
      weekday: t(`share.priceTableDialog.weekdays.${dateObj.getDay()}`)
    });
  };

  return { formatYearMonth, formatMonthDay, formatMonthDayWeekday };
};
